import MobileLayout from "./MobileLayout";
import { persistor } from "../../redux/store";
import logo from "../../assets/logo/cc_logo.png";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SocialIcon } from "react-social-icons";
import {
    IoHomeOutline,
    IoPersonAddOutline,
    IoInformationCircleOutline,
    IoStarOutline,
    IoMailOutline,
    IoShareSocialOutline,
    IoClose,
    IoCopyOutline,
    IoChevronDown,
    IoExpandOutline,
    IoContractOutline,
    IoLogoApple,
    IoMenuOutline,
    IoLocationOutline,
    IoArrowBack,
} from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { setClearHomepageVideos } from "../../redux/ApiSlice/HomePageVideoSlice";
import { joinWaitlist, clearWaitlist } from "../../redux/ApiSlice/WaitlistSlice";
import { postFeedback } from "../../redux/ApiSlice/FeedBackSlice";
import SharePopup from "../Modals/SharePopup";
import { logout } from "../../redux/ApiSlice/Auth/AuthSlice";
import { clearStorageKeepingPreferences } from "../../utils/userPreferences";
import { clearAccountProfile } from "../../redux/ApiSlice/AccountProfileSlice";
import { logoutUser } from "../../redux/ApiSlice/LogoutSlice";

const SHARE_APPS = [
    { id: "facebook", network: "facebook" },
    { id: "whatsapp", network: "whatsapp" },
    { id: "instagram", network: "instagram" },
    { id: "snapchat", network: "snapchat" },
    { id: "x", network: "x" },
    { id: "linkedin", network: "linkedin" },
];

const FEEDBACK_PAGES = [
    "Home",
    "Message",
    "Account profile",
    "Other user account",
    "Neighborhood",
    "My drafts",
    "Upload videos",
    "Settings",
    "Notifications",
    "Search",
    "Groups",
    "Create group",
    "Create video",
    "Create account",
    "Login",
    "Livestreams",
    "Other",
];

export default function WebLayout({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const normalizedPathname = location.pathname.replace(/\/$/, "") || "/";

    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const isOnAboutUs = location.pathname === "/about-us" || location.pathname.startsWith("/about-us/");
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [showDownloadPopup, setShowDownloadPopup] = useState(false);
    const [copied, setCopied] = useState(false);
    const [helpPanelOpen, setHelpPanelOpen] = useState(false);
    const [helpPanelExpanded, setHelpPanelExpanded] = useState(false);
    const [feedbackPage, setFeedbackPage] = useState("");
    const [feedbackPageDropdownOpen, setFeedbackPageDropdownOpen] = useState(false);
    const [feedbackIssueType, setFeedbackIssueType] = useState("Visual");
    const [feedbackDescription, setFeedbackDescription] = useState("");
    const [feedbackEmail, setFeedbackEmail] = useState("");
    const [feedbackName, setFeedbackName] = useState("");
    const [feedbackScreenshot, setFeedbackScreenshot] = useState(null);
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
    const screenshotInputRef = useRef(null);

    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    const [showLocationPopup, setShowLocationPopup] = useState(false);
    const [showWaitlistSidebar, setShowWaitlistSidebar] = useState(false);
    const [waitlistName, setWaitlistName] = useState("");
    const [waitlistEmail, setWaitlistEmail] = useState("");
    const [waitlistLocation, setWaitlistLocation] = useState("");
    const [waitlistPhone, setWaitlistPhone] = useState("");
    const [waitlistInterests, setWaitlistInterests] = useState([]);
    const WAITLIST_COUNTRY_CODE = "+1";

    const { loading: waitlistLoading, message: waitlistMessage, error: waitlistError } = useSelector((state) => state.waitlist);
    const { loading: feedbackLoading } = useSelector((state) => state.feedback);
    const [waitlistCountdown, setWaitlistCountdown] = useState(null);
    const isDuplicateWaitlist = waitlistMessage?.toLowerCase().includes("already");
    const auth = useSelector((state) => state.auth);

    useEffect(() => {
        if (!waitlistMessage) return;
        setWaitlistCountdown(10);
        const interval = setInterval(() => {
            setWaitlistCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    handleWaitlistClose();
                    return null;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [waitlistMessage]);

    const WAITLIST_INTERESTS = [
        { label: "Discover local videos", value: "local_videos" },
        { label: "Share content with neighbors", value: "share" },
        { label: "Community groups", value: "groups" },
        { label: "Finding local events", value: "events" },
        { label: "Just exploring", value: "explore" },
    ];

    const toggleWaitlistInterest = (value) => {
        setWaitlistInterests((prev) =>
            prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value]
        );
    };

    const formatWaitlistPhoneDisplay = (digits) => {
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    };

    const handleWaitlistPhoneChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
        setWaitlistPhone(digits);
    };

    const isWaitlistPhoneValid = waitlistPhone.length === 10;

    const handleWaitlistSubmit = () => {
        if (!waitlistName.trim() || !isWaitlistPhoneValid) return;
        dispatch(joinWaitlist({
            name: waitlistName.trim(),
            email: waitlistEmail.trim(),
            location: waitlistLocation.trim(),
            phone_number: `${WAITLIST_COUNTRY_CODE}${waitlistPhone}`,
            interests: waitlistInterests,
        }));
    };

    const handleWaitlistClose = () => {
        setShowWaitlistSidebar(false);
        dispatch(clearWaitlist());
        setWaitlistName("");
        setWaitlistEmail("");
        setWaitlistLocation("");
        setWaitlistPhone("");
        setWaitlistInterests([]);
        setWaitlistCountdown(null);
    };

    const resetFeedbackForm = () => {
        setFeedbackPage("");
        setFeedbackIssueType("Visual");
        setFeedbackDescription("");
        setFeedbackEmail("");
        setFeedbackName("");
        setFeedbackScreenshot(null);
        setFeedbackSubmitted(false);
    };

    const handleFeedbackClose = () => {
        setHelpPanelOpen(false);
        resetFeedbackForm();
    };

    const handleFeedbackSubmit = async () => {
        if (!feedbackPage || !feedbackDescription.trim()) return;
        const formData = new FormData();
        formData.append("name", feedbackName.trim());
        formData.append("email", feedbackEmail.trim());
        formData.append("page", feedbackPage);
        formData.append("issue", feedbackDescription.trim());
        const issueTypeMap = { "Visual": "visual", "Usability": "usability", "Feature request": "feature_request" };
        formData.append("issue_type", issueTypeMap[feedbackIssueType] ?? feedbackIssueType.toLowerCase());
        if (feedbackScreenshot) formData.append("screenshot", feedbackScreenshot);
        const result = await dispatch(postFeedback({ formData }));
        if (result.meta.requestStatus === "fulfilled") {
            resetFeedbackForm();
            setFeedbackSubmitted(true);
        }
    };

    const shareLink = useMemo(() => {
        if (typeof window === "undefined") {
            return "https://chaachingg.com";
        }
        // return `${window.location.origin}/invite`;
        return "https://chaachingg.com";
    }, []);

    useEffect(() => {
        setIsLoggedIn(!!auth.accessToken);
    }, [location.pathname]);

    useEffect(() => {
        const openWaitlistSidebar = () => setShowWaitlistSidebar(true);

        window.addEventListener("open-waitlist-sidebar", openWaitlistSidebar);
        return () => {
            window.removeEventListener("open-waitlist-sidebar", openWaitlistSidebar);
        };
    }, []);

    useEffect(() => {
        const pref = localStorage.getItem("locationPermission");
        if (pref === "blocked" || pref === "allowed") return;
        if (!navigator.permissions) return;
        navigator.permissions.query({ name: "geolocation" }).then((result) => {
            if (result.state === "prompt") {
                setShowLocationPopup(true);
            }
        });
    }, []);

    const handleBlockLocation = () => {
        localStorage.setItem("locationPermission", "blocked");
        setShowLocationPopup(false);
    };

    const handleAllowLocation = () => {
        setShowLocationPopup(false);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                () => {
                    localStorage.setItem("locationPermission", "allowed");
                },
                () => {
                    localStorage.removeItem("locationPermission");
                },
                { timeout: 10000 }
            );
        }
    };

    const handleAskLocation = () => {
        setShowLocationPopup(false);
    };

    const handleLogOut = async () => {
        const keepLogin = localStorage.getItem("keepMeLogin");
        localStorage.removeItem('accessToken');
        if (keepLogin) {
            localStorage.removeItem("security_login");
        } else {
            clearStorageKeepingPreferences();
            persistor.purge();
        }
        setIsLoggedIn(false);
        dispatch(setClearHomepageVideos());
        await dispatch(logoutUser());
        await dispatch(logout());
        await persistor.flush();
        await persistor.purge();
        await dispatch(clearAccountProfile());
        localStorage.removeItem('accessToken');
        if (keepLogin) {
            localStorage.removeItem("security_login");
            localStorage.removeItem("persist:root");
        } else {
            clearStorageKeepingPreferences();
        }
        navigate("/login");
    };

    const handleCopyShareLink = async () => {
        try {
            await navigator.clipboard.writeText(shareLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        } catch (error) {
            setCopied(false);
        }
    };

    const handleWaitlist = () => {
        setShowWaitlistSidebar(true);
    };

    const removeRightPaddingRoutes =
        location.pathname === "/about-us" ||
        normalizedPathname === "/about-us/why-we-exist" ||
        normalizedPathname === "/about-us/what-makes-us-different" ||
        normalizedPathname === "/about-us/early-community" ||
        normalizedPathname === "/about-us/our-team" ||
        normalizedPathname === "/about-us/get-early-access" ||
        normalizedPathname === "/about-us/social-proof"

    const isFullBleedRoute =
        location.pathname === "/" ||
        location.pathname === "/reel" ||
        location.pathname === "/contact-us" ||
        location.pathname === "/our-features" ||
        location.pathname === "/privacy-policy" ||
        location.pathname === "/terms-of-use" ||
        location.pathname === "/community-guidelines" ||
        location.pathname === "/support" ||
        location.pathname === "/trust-and-safety" ||
        location.pathname === "/about-us" ||
        normalizedPathname === "/about-us/why-we-exist" ||
        normalizedPathname === "/about-us/what-makes-us-different" ||
        normalizedPathname === "/about-us/early-community" ||
        normalizedPathname === "/about-us/our-team" ||
        normalizedPathname === "/about-us/get-early-access" ||
        normalizedPathname === "/about-us/social-proof" ||
        location.pathname === "/join-our-waitlist" ||
        matchPath("/v/:videoId", location.pathname) ||
        matchPath("/watch/:id", location.pathname);

    const isFullWidth =
        normalizedPathname === "/contact-us" ||
        normalizedPathname === "/our-features" ||
        normalizedPathname === "/privacy-policy" ||
        normalizedPathname === "/terms-of-use" ||
        normalizedPathname === "/community-guidelines" ||
        normalizedPathname === "/trust-and-safety" ||
        normalizedPathname === "/support" ||
        normalizedPathname === "/about-us" ||
        normalizedPathname === "/about-us/why-we-exist" ||
        normalizedPathname === "/about-us/what-makes-us-different" ||
        normalizedPathname === "/about-us/early-community" ||
        normalizedPathname === "/about-us/our-team" ||
        normalizedPathname === "/about-us/get-early-access" ||
        normalizedPathname === "/about-us/social-proof" ||
        normalizedPathname === "/join-our-waitlist";

    const isDownloadButtonRoute = normalizedPathname === "/" || !!matchPath("/v/:videoId", location.pathname);

    const sideLinks = [
        {
            label: "Home",
            icon: <IoHomeOutline className="text-[16px]" />,
            action: () => { navigate("/"); },
            isActive: location.pathname === "/",
        },
        ...(!isLoggedIn ? [{
            label: "Create an account",
            icon: <IoPersonAddOutline className="text-[16px]" />,
            action: () => { navigate("/create-account-with"); },
            isActive: location.pathname === "/create-account-with",
        }] : []),
        {
            label: "About us",
            icon: <IoInformationCircleOutline className="text-[16px]" />,
            action: () => {
                navigate("/about-us");
            },
            isActive: isOnAboutUs,
            subItems: [
                { label: "Why we exist", path: "/about-us/why-we-exist" },
                { label: "What makes us different", path: "/about-us/what-makes-us-different" },
                { label: "Early community", path: "/about-us/early-community" },
                { label: "Get early access", path: "/about-us/get-early-access" },
                { label: "Our team", path: "/about-us/our-team" },
                // { label: "Social proof", path: "/about-us/social-proof" },
            ],
        },
        {
            label: "Our features",
            icon: <IoStarOutline className="text-[16px]" />,
            action: () => { navigate("/our-features"); },
            isActive: location.pathname === "/our-features",
        },
        {
            label: "Contact us",
            icon: <IoMailOutline className="text-[16px]" />,
            action: () => { navigate("/contact-us"); },
            isActive: location.pathname === "/contact-us",
        },
    ];

    return (
        <section className="w-full h-[100dvh] bg-[#4A4A4A]">
            <div className="relative h-full w-full bg-[#F4F5F7] overflow-hidden">
                {/* Mobile hamburger button — only for guests on the home landing page */}
                {!isLoggedIn && (location.pathname === "/" || location.pathname.startsWith("/v/")) && (
                    <button
                        type="button"
                        onClick={() => setMobileSidebarOpen(true)}
                        className="flex lg:hidden fixed top-12 left-3 z-50 items-center justify-center w-10 h-10 rounded-xl bg-white/80 backdrop-blur-sm shadow-md text-[#184268]"
                        aria-label="Open menu"
                    >
                        <IoMenuOutline className="text-[24px]" />
                    </button>
                )}

                {/* Mobile sidebar drawer */}
                <AnimatePresence>
                    {mobileSidebarOpen && (
                        <>
                            <motion.div
                                key="mobile-sidebar-backdrop"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="fixed inset-0 z-[60] bg-black/40 lg:hidden"
                                onClick={() => setMobileSidebarOpen(false)}
                            />
                            <motion.aside
                                key="mobile-sidebar-panel"
                                initial={{ x: -300 }}
                                animate={{ x: 0 }}
                                exit={{ x: -300 }}
                                transition={{ type: "spring", stiffness: 320, damping: 30, mass: 0.8 }}
                                className="fixed left-0 top-0 h-full w-[280px] z-[70] bg-[#F4F5F7] flex flex-col justify-between px-7 py-6 lg:hidden overflow-y-auto"
                            >
                                <div>
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center gap-3">
                                            <img src={logo} alt="Chaa Chingg Logo" className="w-10 h-10 object-contain" />
                                            <div>
                                                <p className="text-[20px] font-extrabold text-[#184268] leading-none">Chaa Chingg</p>
                                                <p className="mt-1 text-[11px] font-semibold text-[#5BA9DC] whitespace-nowrap">Connecting Communities</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setMobileSidebarOpen(false)}
                                            className="p-1 rounded-md text-[#5D5D5D] hover:bg-[#E5E5E5]"
                                            aria-label="Close menu"
                                        >
                                            <IoClose className="text-[22px]" />
                                        </button>
                                    </div>

                                    {/* Login / Logout buttons */}
                                    <div className="flex flex-col gap-2 mb-6">
                                        {isLoggedIn ? (
                                            <button
                                                onClick={() => { handleLogOut(); setMobileSidebarOpen(false); }}
                                                className="h-10 w-full rounded-xl border bg-[#F4F5F7] border-[#95CAE9] text-[14px] font-semibold text-[#365975] hover:bg-[#EAF7FF]"
                                            >
                                                Log out
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => { navigate("/login"); setMobileSidebarOpen(false); }}
                                                    className="h-10 w-full rounded-xl border bg-[#F4F5F7] border-[#95CAE9] text-[14px] font-semibold text-black hover:bg-[#EAF7FF]"
                                                >
                                                    Log in
                                                </button>
                                                <button
                                                    onClick={() => { handleWaitlist(); setMobileSidebarOpen(false); }}
                                                    className="h-10 w-full rounded-xl bg-[#BEE6FF] text-[14px] font-semibold text-black hover:bg-[#A8DAFA]"
                                                >
                                                    Join our waitlist
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    {isDownloadButtonRoute && (
                                        <button
                                            type="button"
                                            onClick={() => { setShowDownloadPopup(true); setMobileSidebarOpen(false); }}
                                            className="flex mb-6 h-10 w-full px-6 rounded-xl bg-[#BEE6FF] text-[14px] font-semibold text-black hover:bg-[#A8DAFA] items-center justify-center"
                                        >
                                            Download App
                                        </button>
                                    )}

                                    <div className="flex flex-col gap-1">
                                        {sideLinks.map((item) => (
                                            <div key={item.label}>
                                                <button
                                                    onClick={() => {
                                                        item.action();
                                                        if (!item.subItems) {
                                                            setMobileSidebarOpen(false);
                                                        }
                                                    }}
                                                    className={`w-full flex items-center gap-3 rounded-lg px-2 py-2 text-left text-[15px] font-semibold transition-colors ${item.isActive ? "text-[#2E9DDA]" : "text-[#3F3F3F] hover:text-[#2E9DDA]"}`}
                                                >
                                                    {item.icon}
                                                    <span>{item.label}</span>
                                                </button>
                                                {item.subItems && (
                                                    <div className="ml-7 flex flex-col gap-0.5 pb-1">
                                                        {item.subItems.map((sub) => (
                                                            <button
                                                                key={sub.path}
                                                                onClick={() => { navigate(sub.path); setMobileSidebarOpen(false); }}
                                                                className={`text-left rounded-lg px-2 py-1.5 text-[13px] font-bold transition-colors ${location.pathname === sub.path ? "text-[#2E9DDA]" : "text-[#3F3F3F] hover:text-[#2E9DDA]"}`}
                                                            >
                                                                {sub.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="text-[#3F3F3F]">
                                    <button
                                        className="w-full mb-3 rounded-xl border border-[#B6DBEE] bg-[#F8FBFE] px-4 py-3 text-left"
                                        onClick={() => { setMobileSidebarOpen(false); setHelpPanelOpen(true); }}
                                    >
                                        <p className="text-[13px] font-semibold text-[#365975]">Help shape Chaa Chingg</p>
                                        <p className="mt-0.5 text-[11px] text-[#587085]">Your feedback may influence what we build next!</p>
                                    </button>
                                    <button className="flex items-center gap-2 text-[14px] font-semibold" onClick={() => { setShowSharePopup(true); setMobileSidebarOpen(false); }}>
                                        <IoShareSocialOutline className="text-[15px]" />
                                        <span>Share</span>
                                    </button>
                                    <p onClick={() => { navigate("/privacy-policy"); setMobileSidebarOpen(false); }} className={`mt-3 cursor-pointer text-[14px] font-semibold hover:text-[#2E9DDA] ${location.pathname === "/privacy-policy" ? "text-[#2E9DDA]" : ""}`}>
                                        Privacy Policy
                                    </p>
                                    <p onClick={() => { navigate("/terms-of-use"); setMobileSidebarOpen(false); }} className={`mt-3 cursor-pointer text-[14px] font-semibold hover:text-[#2E9DDA] ${location.pathname === "/terms-of-use" ? "text-[#2E9DDA]" : ""}`}>
                                        Terms and Conditions
                                    </p>
                                    <p onClick={() => { navigate("/community-guidelines"); setMobileSidebarOpen(false); }} className={`mt-3 cursor-pointer text-[14px] font-semibold hover:text-[#2E9DDA] ${location.pathname === "/community-guidelines" ? "text-[#2E9DDA]" : ""}`}>
                                        Community Guidelines
                                    </p>
                                    <p onClick={() => { navigate("/trust-and-safety"); setMobileSidebarOpen(false); }} className={`mt-3 cursor-pointer text-[14px] font-semibold hover:text-[#2E9DDA] ${location.pathname === "/trust-and-safety" ? "text-[#2E9DDA]" : ""}`}>
                                        Trust & Safety Center
                                    </p>
                                    <p onClick={() => { navigate("/support"); setMobileSidebarOpen(false); }} className={`mt-3 cursor-pointer text-[14px] font-semibold hover:text-[#2E9DDA] ${location.pathname === "/support" ? "text-[#2E9DDA]" : ""}`}>
                                        Help & Support
                                    </p>
                                    <p className="mt-3 text-[14px] font-semibold whitespace-nowrap">© 2026 The Chaa Chingg Company</p>
                                </div>
                            </motion.aside>
                        </>
                    )}
                </AnimatePresence>

                {/* Mobile feedback bottom sheet */}
                <AnimatePresence>
                    {helpPanelOpen && (
                        <>
                            <motion.div
                                key="mobile-feedback-backdrop"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="fixed inset-0 z-[60] bg-black/40 xl:hidden"
                                onClick={handleFeedbackClose}
                            />
                            <motion.div
                                key="mobile-feedback-sheet"
                                initial={{ y: "100%" }}
                                animate={{ y: 0 }}
                                exit={{ y: "100%" }}
                                transition={{ type: "spring", stiffness: 320, damping: 30, mass: 0.8 }}
                                className="fixed bottom-0 left-0 right-0 z-[70] bg-[#F8FBFE] rounded-t-2xl border-t border-[#B6DBEE] shadow-xl xl:hidden max-h-[85vh] flex flex-col"
                            >
                                <div className="flex items-start justify-between px-5 pt-5 pb-3">
                                    <div>
                                        <p className="text-[15px] leading-none font-semibold text-black">Help shape Chaa Chingg</p>
                                        <p className="mt-1.5 text-[12px] text-gray-700">Your feedback may influence what we build next.</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleFeedbackClose}
                                        className="rounded-md p-1 text-[#587085] hover:bg-[#EAF6FD]"
                                        aria-label="Close feedback panel"
                                    >
                                        <IoClose className="text-[20px]" />
                                    </button>
                                </div>

                                <div className="feedback-panel-scrollbar overflow-y-auto px-5 pb-6">
                                    <div className="border-t border-[#D8E9F3] pt-4">
                                        <label className="text-[12px] font-semibold text-black">
                                            Where did this happen?<span className="text-[#D94343]">*</span>
                                        </label>
                                        <div
                                            className="relative mt-1.5"
                                            onBlur={(event) => {
                                                if (!event.currentTarget.contains(event.relatedTarget)) {
                                                    setFeedbackPageDropdownOpen(false);
                                                }
                                            }}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => setFeedbackPageDropdownOpen((prev) => !prev)}
                                                className="flex h-10 w-full items-center justify-between rounded-lg border border-[#D5E7F2] bg-white px-3 text-left text-[12px] text-[#334D60] outline-none"
                                            >
                                                <span className={feedbackPage ? "text-[#334D60]" : "text-[#7A8A95]"}>{feedbackPage || "Select a page"}</span>
                                                <motion.span
                                                    animate={{ rotate: feedbackPageDropdownOpen ? 180 : 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="inline-flex"
                                                >
                                                    <IoChevronDown className="text-[14px] text-[#587085]" />
                                                </motion.span>
                                            </button>
                                            <AnimatePresence>
                                                {feedbackPageDropdownOpen && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: -4 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -4 }}
                                                        transition={{ duration: 0.16 }}
                                                        className="absolute left-0 right-0 z-20 mt-1 rounded-lg border border-[#D5E7F2] bg-white shadow-sm"
                                                    >
                                                        <div className="feedback-dropdown-scrollbar max-h-[12rem] overflow-y-auto py-1">
                                                            {FEEDBACK_PAGES.map((page) => (
                                                                <button
                                                                    key={page}
                                                                    type="button"
                                                                    onClick={() => { setFeedbackPage(page); setFeedbackPageDropdownOpen(false); }}
                                                                    className={`block w-full px-3 py-1.5 text-left text-[12px] ${feedbackPage === page ? "bg-[#EAF6FD] text-[#244A63]" : "text-[#334D60] hover:bg-[#F5FAFE]"}`}
                                                                >
                                                                    {page}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </div>

                                    <div className="mt-4 border-t border-[#D8E9F3] pt-4">
                                        <p className="text-[12px] font-semibold text-black">
                                            What type of issue?<span className="text-[#D94343]">*</span>
                                        </p>
                                        <div className="mt-1.5 flex flex-wrap gap-2">
                                            {["Visual", "Usability", "Feature request"].map((type) => (
                                                <button
                                                    key={type}
                                                    type="button"
                                                    onClick={() => setFeedbackIssueType(type)}
                                                    className={`h-8 rounded-md border px-3 text-[12px] ${feedbackIssueType === type ? "border-[#9FCFEA] bg-[#BEE6FF] text-[#244A63]" : "border-[#BFDDEE] bg-white text-[#365975]"}`}
                                                >
                                                    {type}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mt-4 border-t border-[#D8E9F3] pt-4">
                                        <label className="text-[12px] font-semibold text-black">
                                            Describe what happened<span className="text-[#D94343]">*</span>
                                        </label>
                                        <textarea
                                            value={feedbackDescription}
                                            onChange={(event) => setFeedbackDescription(event.target.value.slice(0, 500))}
                                            placeholder="What happened? What did you expect instead? Include steps if possible."
                                            className="mt-1.5 h-24 w-full resize-none rounded-lg border border-[#D5E7F2] bg-white px-3 py-2 text-[12px] text-black outline-none"
                                        />
                                        <p className="mt-1 text-right text-[12px] text-[#7A8A95]">{feedbackDescription.length}/500</p>
                                    </div>

                                    <div className="mt-4 border-t border-[#D8E9F3] pt-4">
                                        <p className="text-[12px] font-semibold text-black">Add screenshot (optional)</p>
                                        <input
                                            ref={screenshotInputRef}
                                            type="file"
                                            accept="image/png, image/jpeg"
                                            className="hidden"
                                            onChange={(e) => setFeedbackScreenshot(e.target.files?.[0] || null)}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => screenshotInputRef.current?.click()}
                                            className="mt-1.5 rounded-md border border-[#9FCFEA] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#365975]"
                                        >
                                            {feedbackScreenshot ? feedbackScreenshot.name : "Choose image (PNG or JPG, Max 5MB)"}
                                        </button>
                                    </div>

                                    <div className="mt-4 border-t border-[#D8E9F3] pt-4">
                                        <p className="text-[12px] font-semibold text-black">Stay in touch (optional)</p>
                                        <label className="mt-3 block text-[12px] font-semibold text-black">Email</label>
                                        <input
                                            value={feedbackEmail}
                                            onChange={(event) => setFeedbackEmail(event.target.value)}
                                            placeholder="Enter your email"
                                            className="mt-1.5 h-10 w-full rounded-lg border border-[#D5E7F2] bg-white px-3 text-[12px] text-black outline-none"
                                        />
                                        <p className="mt-1 text-[12px] text-[#7A8A95]">We'll only contact you if clarification is needed.</p>
                                        <label className="mt-4 block text-[12px] font-semibold text-black">Name</label>
                                        <input
                                            value={feedbackName}
                                            onChange={(event) => setFeedbackName(event.target.value)}
                                            placeholder="Your name"
                                            className="mt-1.5 h-10 w-full rounded-lg border border-[#D5E7F2] bg-white px-3 text-[12px] text-black outline-none"
                                        />
                                    </div>

                                    {feedbackSubmitted && (
                                        <p className="mt-4 text-[13px] font-semibold text-green-600">Thank you! Your feedback has been submitted.</p>
                                    )}

                                    <button
                                        type="button"
                                        onClick={handleFeedbackSubmit}
                                        disabled={feedbackLoading || !feedbackPage || !feedbackDescription.trim()}
                                        className="mt-5 h-11 w-full rounded-xl bg-[#BEE6FF] text-[14px] font-semibold text-black hover:bg-[#A8DAFA] disabled:opacity-60"
                                    >
                                        {feedbackLoading ? "Submitting..." : "Submit feedback"}
                                    </button>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>

                {/* Sidebar */}
                <aside className="hidden lg:flex absolute left-0 top-0 h-full w-[260px] flex-col justify-between px-7 py-6">
                    <div>
                        <div className="flex items-center gap-3">
                            <img src={logo} alt="Chaa Chingg Logo" className="w-12 h-12 object-contain" />
                            <div>
                                <p className="text-[24px] font-extrabold text-[#184268] leading-none">Chaa Chingg</p>
                                <p className="mt-1 text-[12px] font-semibold text-[#5BA9DC] whitespace-nowrap">Connecting Communities</p>
                            </div>
                        </div>

                        {isDownloadButtonRoute && (
                            <button
                                type="button"
                                onClick={() => setShowDownloadPopup(true)}
                                className="hidden lg:flex mt-5 h-10 px-6 rounded-xl bg-[#BEE6FF] text-[14px] font-semibold text-black hover:bg-[#A8DAFA] items-center"
                            >
                                Download App
                            </button>
                        )}

                        <div className="mt-8 flex flex-col gap-1">
                            {sideLinks.map((item) => (
                                <div key={item.label}>
                                    <button
                                        onClick={() => item.action()}
                                        className={`w-full flex items-center gap-3 rounded-lg px-2 py-2 text-left text-[15px] font-semibold transition-colors ${item.isActive
                                            ? "text-[#2E9DDA]"
                                            : "text-[#3F3F3F] hover:text-[#2E9DDA]"
                                            }`}
                                    >
                                        {item.icon}
                                        <span>{item.label}</span>
                                    </button>
                                    {item.subItems && (
                                        <div className="ml-7 flex flex-col gap-0.5 pb-1">
                                            {item.subItems.map((sub) => (
                                                <button
                                                    key={sub.path}
                                                    onClick={() => navigate(sub.path)}
                                                    className={`text-left rounded-lg px-2 py-1.5 text-[13px] font-bold transition-colors ${location.pathname === sub.path
                                                        ? "text-[#2E9DDA]"
                                                        : "text-[#3F3F3F] hover:text-[#2E9DDA]"
                                                        }`}
                                                >
                                                    {sub.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="text-[#3F3F3F]">
                        <button className="flex items-center gap-2 text-[14px] font-semibold" onClick={() => setShowSharePopup(true)}>
                            <IoShareSocialOutline className="text-[15px]" />
                            <span>Share</span>
                        </button>
                        <p onClick={() => { navigate("/trust-and-safety"); setMobileSidebarOpen(false); }} className={`mt-3 cursor-pointer text-[14px] font-semibold hover:text-[#2E9DDA] ${location.pathname === "/trust-and-safety" ? "text-[#2E9DDA]" : ""}`}>
                            Trust & Safety Center
                        </p>
                        <p onClick={() => navigate("/support")} className={`mt-3 cursor-pointer text-[14px] font-semibold hover:text-[#2E9DDA] ${location.pathname === "/support" ? "text-[#2E9DDA]" : ""}`}>
                            Help & Support
                        </p>
                        <p className="mt-3 text-[14px] font-semibold whitespace-nowrap">© 2026 The Chaa Chingg Company</p>
                    </div>
                </aside>

                <div className="hidden lg:flex absolute top-6 right-6 items-center gap-3">
                    {isLoggedIn ? (
                        <button
                            onClick={handleLogOut}
                            className="h-11 px-7 rounded-xl border bg-[#F4F5F7] border-[#95CAE9] text-[16px] font-semibold text-[#365975] hover:bg-[#EAF7FF]"
                        >
                            Log out
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={() => navigate("/login")}
                                className="h-10 px-6 rounded-xl border bg-[#F4F5F7] border-[#95CAE9] text-[14px] font-semibold text-black hover:bg-[#EAF7FF]"
                            >
                                Log in
                            </button>
                            <button
                                onClick={handleWaitlist}
                                className="h-10 px-6 rounded-xl bg-[#BEE6FF] text-[14px] font-semibold text-black hover:bg-[#A8DAFA]"
                            >
                                Join our waitlist
                            </button>
                        </>
                    )}
                </div>

                <div className={`h-full w-full ${removeRightPaddingRoutes ? "lg:pr-[0px]" : "lg:pr-[0px] xl:pr-[280px]"} lg:pl-[260px] flex items-center justify-center`}>
                    <div className={`flex justify-center relative overflow-y-scroll no-scrollbar h-full w-full ${!isFullWidth && "md:h-[93%] md:w-[80%] lg:w-[78%] xl:w-[72%] 2xl:w-[62%]"}`}>
                        {isFullBleedRoute ? (
                            children
                        ) : (
                            <div className="flex flex-col bg-white dark:bg-[#1E1D21] border-black md:border-[2px] md:rounded-[20px] h-full w-full md:w-[75%] lg:w-[60%] xl:w-[55%] overflow-hidden relative">
                                <MobileLayout>{children}</MobileLayout>
                            </div>
                        )}
                    </div>
                </div>

                {/* Share popup overlay */}
                <SharePopup
                    isOpen={showSharePopup}
                    onClose={() => setShowSharePopup(false)}
                    shareUrl={shareLink}
                />

                {/* Download app popup overlay */}
                <AnimatePresence>
                    {showDownloadPopup && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25, ease: "easeInOut" }}
                            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 px-4"
                            onClick={() => setShowDownloadPopup(false)}
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 24, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 16, scale: 0.96 }}
                                transition={{ type: "spring", stiffness: 320, damping: 28, mass: 0.8 }}
                                className="w-full max-w-[430px] rounded-2xl bg-white p-5 shadow-xl"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-0.5 rounded-lg bg-[#F1F7FC] p-2 text-[#184268]">
                                            <IoLogoApple className="text-[22px]" />
                                        </div>
                                        <div>
                                            <p className="text-[19px] font-semibold leading-none text-black">How to download Chaa Chingg</p>
                                            <div className="mt-2 text-[14px] text-gray-700">
                                                <p>1. Open Apple App Store on your iPhone.</p>
                                                <p>2. Search for Chaa Chingg.</p>
                                                <p>3. Tap Install, then open the app after install.</p>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowDownloadPopup(false)}
                                        className="rounded-md p-1 text-[#5D5D5D] hover:bg-[#F3F3F3]"
                                        aria-label="Close download app popup"
                                    >
                                        <IoClose className="text-[22px]" />
                                    </button>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => setShowDownloadPopup(false)}
                                    className="mt-5 h-10 w-full rounded-md bg-[#BEE6FF] hover:bg-[#A8DAFA] text-[16px] font-semibold text-black"
                                >
                                    Okay
                                </button>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Feedback panel */}
                {!isFullWidth && (
                    <div className="hidden xl:flex absolute bottom-6 right-6 z-30 flex-col items-end gap-4">
                        <AnimatePresence mode="wait">
                            {helpPanelOpen ? (
                                <motion.div
                                    key="help-expanded"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 20 }}
                                    transition={{ duration: 0.24 }}
                                    className={`rounded-2xl border border-[#B6DBEE] bg-[#F8FBFE] shadow-sm ${helpPanelExpanded ? "w-[430px]" : "w-[320px]"}`}
                                >
                                    <div className="flex items-start justify-between px-4 pt-4">
                                        <div>
                                            <p className="text-[14px] leading-none font-semibold text-black">Help shape Chaa Chingg</p>
                                            <p className="mt-2 text-[12px] text-gray-700">Your feedback may influence what we build next.</p>
                                        </div>
                                        <div className="flex items-center gap-1 text-[#587085]">
                                            <button
                                                type="button"
                                                onClick={() => setHelpPanelExpanded((prev) => !prev)}
                                                className="rounded-md p-1 hover:bg-[#EAF6FD]"
                                                aria-label="Toggle expanded feedback panel"
                                            >
                                                {helpPanelExpanded ? <IoContractOutline className="text-[16px]" /> : <IoExpandOutline className="text-[16px]" />}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleFeedbackClose}
                                                className="rounded-md p-1 hover:bg-[#EAF6FD]"
                                                aria-label="Close feedback panel"
                                            >
                                                <IoClose className="text-[16px]" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="feedback-panel-scrollbar mt-3 max-h-[58vh] overflow-y-auto px-4 pb-4">
                                        <div className="border-t border-[#D8E9F3] pt-4">
                                            <label className="text-[12px] font-semibold text-black">
                                                Where did this happen?<span className="text-[#D94343]">*</span>
                                            </label>
                                            <div
                                                className="relative mt-1.5"
                                                onBlur={(event) => {
                                                    if (!event.currentTarget.contains(event.relatedTarget)) {
                                                        setFeedbackPageDropdownOpen(false);
                                                    }
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => setFeedbackPageDropdownOpen((prev) => !prev)}
                                                    className="flex h-10 w-full items-center justify-between rounded-lg border border-[#D5E7F2] bg-white px-3 text-left text-[12px] text-[#334D60] outline-none"
                                                    aria-haspopup="listbox"
                                                    aria-expanded={feedbackPageDropdownOpen}
                                                >
                                                    <span className={feedbackPage ? "text-[#334D60]" : "text-[#7A8A95]"}>{feedbackPage || "Select a page"}</span>
                                                    <motion.span
                                                        animate={{ rotate: feedbackPageDropdownOpen ? 180 : 0 }}
                                                        transition={{ duration: 0.2 }}
                                                        className="inline-flex"
                                                    >
                                                        <IoChevronDown className="text-[14px] text-[#587085]" />
                                                    </motion.span>
                                                </button>

                                                <AnimatePresence>
                                                    {feedbackPageDropdownOpen && (
                                                        <motion.div
                                                            initial={{ opacity: 0, y: -4 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            exit={{ opacity: 0, y: -4 }}
                                                            transition={{ duration: 0.16 }}
                                                            className="absolute left-0 right-0 z-20 mt-1 rounded-lg border border-[#D5E7F2] bg-white shadow-sm"
                                                        >
                                                            <div className="feedback-dropdown-scrollbar max-h-[15rem] overflow-y-auto py-1">
                                                                {FEEDBACK_PAGES.map((page) => (
                                                                    <button
                                                                        key={page}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setFeedbackPage(page);
                                                                            setFeedbackPageDropdownOpen(false);
                                                                        }}
                                                                        className={`block w-full px-3 py-1.5 text-left text-[12px] ${feedbackPage === page ? "bg-[#EAF6FD] text-[#244A63]" : "text-[#334D60] hover:bg-[#F5FAFE]"}`}
                                                                    >
                                                                        {page}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        </div>

                                        <div className="mt-4 border-t border-[#D8E9F3] pt-4">
                                            <p className="text-[12px] font-semibold text-black">
                                                What type of issue?<span className="text-[#D94343]">*</span>
                                            </p>
                                            <div className="mt-1.5 flex flex-wrap gap-2">
                                                {["Visual", "Usability", "Feature request"].map((type) => (
                                                    <button
                                                        key={type}
                                                        type="button"
                                                        onClick={() => setFeedbackIssueType(type)}
                                                        className={`h-8 rounded-md border px-3 text-[12px] ${feedbackIssueType === type
                                                            ? "border-[#9FCFEA] bg-[#BEE6FF] text-[#244A63]"
                                                            : "border-[#BFDDEE] bg-white text-[#365975]"
                                                            }`}
                                                    >
                                                        {type}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="mt-4 border-t border-[#D8E9F3] pt-4">
                                            <label className="text-[12px] font-semibold text-black">
                                                Describe what happened<span className="text-[#D94343]">*</span>
                                            </label>
                                            <textarea
                                                value={feedbackDescription}
                                                onChange={(event) => setFeedbackDescription(event.target.value.slice(0, 500))}
                                                placeholder="What happened? What did you expect instead? Include steps if possible."
                                                className="mt-1.5 h-24 w-full resize-none rounded-lg border border-[#D5E7F2] bg-white px-3 py-2 text-[12px] text-black outline-none"
                                            />
                                            <p className="mt-1 text-right text-[12px] text-[#7A8A95]">{feedbackDescription.length}/500</p>
                                        </div>

                                        <div className="mt-4 border-t border-[#D8E9F3] pt-4">
                                            <p className="text-[12px] font-semibold text-black">Add screenshot (optional)</p>
                                            <button
                                                type="button"
                                                onClick={() => screenshotInputRef.current?.click()}
                                                className="mt-1.5 rounded-md border border-[#9FCFEA] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#365975]"
                                            >
                                                {feedbackScreenshot ? feedbackScreenshot.name : "Choose image (PNG or JPG, Max 5MB)"}
                                            </button>
                                        </div>

                                        <div className="mt-4 border-t border-[#D8E9F3] pt-4">
                                            <p className="text-[12px] font-semibold text-black">Stay in touch (optional)</p>

                                            <label className="mt-3 block text-[12px] font-semibold text-black">Email</label>
                                            <input
                                                value={feedbackEmail}
                                                onChange={(event) => setFeedbackEmail(event.target.value)}
                                                placeholder="Enter your email"
                                                className="mt-1.5 h-10 w-full rounded-lg border border-[#D5E7F2] bg-white px-3 text-[12px] text-black outline-none"
                                            />
                                            <p className="mt-1 text-[12px] text-[#7A8A95]">We'll only contact you if clarification is needed.</p>

                                            <label className="mt-4 block text-[12px] font-semibold text-black">Name</label>
                                            <input
                                                value={feedbackName}
                                                onChange={(event) => setFeedbackName(event.target.value)}
                                                placeholder="Your name"
                                                className="mt-1.5 h-10 w-full rounded-lg border border-[#D5E7F2] bg-white px-3 text-[12px] text-black outline-none"
                                            />
                                        </div>

                                        {feedbackSubmitted && (
                                            <p className="mt-4 text-[13px] font-semibold text-green-600">Thank you! Your feedback has been submitted.</p>
                                        )}

                                        <button
                                            type="button"
                                            onClick={handleFeedbackSubmit}
                                            disabled={feedbackLoading || !feedbackPage || !feedbackDescription.trim()}
                                            className="mt-5 h-11 w-[75%] rounded-xl bg-[#BEE6FF] text-[14px] font-semibold text-black hover:bg-[#A8DAFA] mx-auto block disabled:opacity-60"
                                        >
                                            {feedbackLoading ? "Submitting..." : "Submit feedback"}
                                        </button>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="help-collapsed"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 20 }}
                                    transition={{ duration: 0.2 }}
                                    className="w-[320px] rounded-2xl border border-[#B6DBEE] bg-[#F8FBFE] px-5 py-4 shadow-sm"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="text-[14px] leading-tight font-semibold text-[#365975]">Help shape Chaa Chingg</p>
                                            <p className="mt-1 text-[12px] text-[#587085]">Your feedback may influence what we build next!</p>
                                        </div>
                                        <IoChevronDown
                                            type="button"
                                            aria-label="Expand feedback card"
                                            onClick={() => setHelpPanelOpen(true)}
                                            className={`text-[25px] cursor-pointer ${helpPanelExpanded ? "rotate-180" : ""} text-[#587085] hover:bg-[#EAF6FD] rounded-md p-1`}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="text-right text-[#3F3F3F]">
                            <p onClick={() => navigate("/privacy-policy")} className={`cursor-pointer text-[14px] font-semibold hover:text-[#2E9DDA] ${location.pathname === "/privacy-policy" ? "text-[#2E9DDA]" : ""}`}>
                                Privacy Policy
                            </p>
                            <p onClick={() => navigate("/terms-of-use")} className={`mt-3 cursor-pointer text-[14px] font-semibold hover:text-[#2E9DDA] ${location.pathname === "/terms-of-use" ? "text-[#2E9DDA]" : ""}`}>
                                Terms and Conditions
                            </p>
                            <p onClick={() => navigate("/community-guidelines")} className={`mt-3 cursor-pointer text-[14px] font-semibold hover:text-[#2E9DDA] ${location.pathname === "/community-guidelines" ? "text-[#2E9DDA]" : ""}`}>
                                Community Guidelines
                            </p>
                        </div>
                    </div>
                )}

                {/* Waitlist sidebar */}
                <AnimatePresence>
                    {showWaitlistSidebar && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="fixed inset-0 z-50 flex justify-end bg-black/45"
                            onClick={handleWaitlistClose}
                        >
                            <motion.div
                                initial={{ x: "100%" }}
                                animate={{ x: 0 }}
                                exit={{ x: "100%" }}
                                transition={{ type: "spring", stiffness: 280, damping: 30, mass: 0.9 }}
                                className="relative h-full w-full max-w-[420px] bg-white shadow-2xl flex flex-col"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Header */}
                                <div className="flex flex-col px-7 pt-7 pb-4 border-b border-gray-200">
                                    {/* Back arrow — mobile only */}
                                    <button
                                        type="button"
                                        onClick={handleWaitlistClose}
                                        className="flex md:hidden items-center gap-1.5 mb-4 flex-shrink-0 text-[#5D5D5D]"
                                        aria-label="Go back"
                                    >
                                        <IoArrowBack className="text-[22px]" />
                                    </button>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-[20px] font-bold text-black leading-tight">Join Chaa Chingg (Beta)</p>
                                            <p className="mt-2 text-[13px] text-[#555] leading-snug max-w-[300px]">
                                                Be among the first to explore local video sharing and help shape the platform before public launch.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleWaitlistClose}
                                            className="hidden md:flex ml-3 mt-0.5 rounded-md p-1 text-[#5D5D5D] hover:bg-[#F3F3F3] flex-shrink-0"
                                            aria-label="Close waitlist panel"
                                        >
                                            <IoClose className="text-[22px]" />
                                        </button>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto">
                                    <div className="px-7 pb-7 border-b border-gray-200">
                                        {/* Name */}
                                        <div className="mt-2">
                                            <label className="text-[13px] font-semibold text-black">
                                                Name<span className="text-[#D94343]">*</span>
                                            </label>
                                            <input
                                                value={waitlistName}
                                                onChange={(e) => setWaitlistName(e.target.value)}
                                                placeholder="Your name"
                                                className="mt-1.5 h-11 w-full rounded-lg border border-[#D5E7F2] bg-white px-3 text-[13px] text-black outline-none focus:border-[#5BA9DC]"
                                            />
                                        </div>

                                        {/* Phone number */}
                                        <div className="mt-4">
                                            <label className="text-[13px] font-semibold text-black">
                                                Phone number<span className="text-[#D94343]">*</span>
                                            </label>
                                            <div className="mt-1.5 flex h-11 w-full items-center gap-2 rounded-lg border border-[#D5E7F2] bg-white px-3 focus-within:border-[#5BA9DC]">
                                                <span className="text-[13px] text-black select-none">{WAITLIST_COUNTRY_CODE}</span>
                                                <input
                                                    value={formatWaitlistPhoneDisplay(waitlistPhone)}
                                                    onChange={handleWaitlistPhoneChange}
                                                    placeholder="234-567-8901"
                                                    type="tel"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    className="flex-1 text-[13px] text-black outline-none bg-transparent"
                                                />
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="mt-4">
                                            <label className="text-[13px] font-semibold text-black">Email</label>
                                            <input
                                                value={waitlistEmail}
                                                onChange={(e) => setWaitlistEmail(e.target.value)}
                                                placeholder="Enter your email"
                                                type="email"
                                                className="mt-1.5 h-11 w-full rounded-lg border border-[#D5E7F2] bg-white px-3 text-[13px] text-black outline-none focus:border-[#5BA9DC]"
                                            />
                                        </div>

                                        {/* Location */}
                                        <div className="mt-4">
                                            <label className="text-[13px] font-semibold text-black">Location</label>
                                            <input
                                                value={waitlistLocation}
                                                onChange={(e) => setWaitlistLocation(e.target.value)}
                                                placeholder="City or neighborhood"
                                                className="mt-1.5 h-11 w-full rounded-lg border border-[#D5E7F2] bg-white px-3 text-[13px] text-black outline-none focus:border-[#5BA9DC]"
                                            />
                                        </div>

                                        {/* Interests */}
                                        <div className="mt-6">
                                            <p className="text-[13px] font-semibold text-black">What are you most excited about?</p>
                                            <div className="mt-3 flex flex-col gap-3">
                                                {WAITLIST_INTERESTS.map(({ label, value }) => (
                                                    <label key={value} className="flex items-center gap-3 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={waitlistInterests.includes(value)}
                                                            onChange={() => toggleWaitlistInterest(value)}
                                                            className="w-4 h-4 rounded border-[#B0CADB] accent-[#2E9DDA] cursor-pointer"
                                                        />
                                                        <span className="text-[13px] text-[#333]">{label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Submit */}
                                    <div className="px-7 pb-7">
                                        {waitlistMessage ? (
                                            <div className={`mt-8 rounded-xl px-5 py-4 text-center ${isDuplicateWaitlist ? "bg-[#FFF0F0]" : "bg-[#E6F7FF]"}`}>
                                                <p className={`text-[14px] font-semibold ${isDuplicateWaitlist ? "text-[#D94343]" : "text-[#1A7AAB]"}`}>
                                                    {waitlistMessage}
                                                </p>
                                                {waitlistCountdown !== null && (
                                                    <p className="mt-1 text-[11px] text-[#999]">Closing in {waitlistCountdown}s</p>
                                                )}
                                            </div>
                                        ) : (
                                            <>
                                                {waitlistError && (
                                                    <p className="mt-4 text-center text-[12px] text-[#D94343]">
                                                        {typeof waitlistError === "string" ? waitlistError : "Something went wrong. Please try again."}
                                                    </p>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={handleWaitlistSubmit}
                                                    disabled={waitlistLoading || !waitlistName.trim() || !isWaitlistPhoneValid}
                                                    className="mt-8 h-12 w-full rounded-xl bg-[#BEE6FF] text-[15px] font-semibold text-black hover:bg-[#A8DAFA] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {waitlistLoading ? "Joining..." : "Join waitlist"}
                                                </button>
                                            </>
                                        )}

                                        <p className="mt-3 text-center text-[12px] text-[#7A8A95]">
                                            We'll notify you when beta spots open in your area.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Location access popup */}
                {/* <AnimatePresence>
                    {showLocationPopup && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25, ease: "easeInOut" }}
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4"
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 24, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 16, scale: 0.96 }}
                                transition={{ type: "spring", stiffness: 320, damping: 28, mass: 0.8 }}
                                className="w-full max-w-[400px] rounded-2xl bg-white p-5 shadow-xl"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex flex-col items-center text-center">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#EAF6FD]">
                                        <IoLocationOutline className="text-[28px] text-[#2E9DDA]" />
                                    </div>
                                    <p className="mt-4 text-[18px] font-bold text-black leading-snug">Allow Location Access?</p>
                                    <p className="mt-2 text-[13px] text-[#555] leading-relaxed max-w-[300px]">
                                        Chaa Chingg uses your location to show nearby content and connect you with your community.
                                    </p>
                                    <p className="mt-1.5 text-[11px] text-[#7A8A95] leading-snug max-w-[300px]">
                                        By default your browser will prompt you each time — choose Allow or Block below to make a permanent choice.
                                    </p>
                                </div>

                                <div className="mt-5 flex flex-col gap-2 lg:flex-row">
                                    <button
                                        type="button"
                                        onClick={handleBlockLocation}
                                        className="flex-1 rounded-xl border border-[#D5E7F2] bg-white px-2.5 py-2.5 text-left hover:bg-[#F4F9FD] transition-colors"
                                    >
                                        <p className="text-[13px] font-semibold text-[#365975]">Block</p>
                                        <p className="mt-0.5 text-[11px] text-[#7A8A95] leading-snug">Prevent location access permanently.</p>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleAllowLocation}
                                        className="flex-1 rounded-xl bg-[#BEE6FF] px-2.5 py-2.5 text-left hover:bg-[#A8DAFA] transition-colors"
                                    >
                                        <p className="text-[13px] font-semibold text-black">Allow</p>
                                        <p className="mt-0.5 text-[11px] text-[#365975] leading-snug">Grant location access now.</p>
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence> */}
            </div>
        </section>
    );
}
